import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tab,
  Tabs,
  Button,
  Stack,
} from "@mui/material";
import { UserContext } from "../context/UserContext";
import API_BASE_URL from "../config";

// Helper function to fetch data for each category
const fetchCategoryData = async (userId, category) => {
  try {
    const url =
      category === "sponsored-ads"
        ? `${API_BASE_URL}/sponsored-ads/user/${userId}`
        : `${API_BASE_URL}/${category}/user/${userId}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${category}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [data];
  } catch (error) {
    console.error(`Error fetching ${category} data:`, error);
    return [];
  }
};

// Helper function to update the status for each category
const updateCategoryStatus = async (id, category, status) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${category}/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `Failed to update ${category} status`);
    }
    return data;
  } catch (error) {
    console.error(`Error updating ${category} status:`, error);
    throw error;
  }
};

const MyOpportunities = () => {
  const { user } = useContext(UserContext);
  const [selectedCategory, setSelectedCategory] = useState("Services");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [services, setServices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [offers, setOffers] = useState([]);
  const [classifieds, setClassifieds] = useState([]);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    if (user && user.user_id) {
      setLoading(true);
      setError(null);
      const categoryKey = selectedCategory.toLowerCase();
      fetchCategoryData(user.user_id, categoryKey)
        .then((data) => {
          switch (selectedCategory) {
            case "Services":
              setServices(data);
              break;
            case "Jobs":
              setJobs(data);
              break;
            case "Events":
              setEvents(data);
              break;
            case "Offers":
              setOffers(data);
              break;
            case "Classifieds":
              setClassifieds(data);
              break;
            case "sponsored-ads":
              setAds(data);
              break;
            default:
              break;
          }
        })
        .catch(() => {
          setError(`Error fetching data for ${selectedCategory}. Please try again.`);
        })
        .finally(() => setLoading(false));
    }
  }, [user, selectedCategory]);

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleStatusChange = async (itemId, newStatus) => {
    const categoryKey = selectedCategory.toLowerCase();
    try {
      await updateCategoryStatus(itemId, categoryKey, newStatus);

      const updateItem = (items) =>
        items.map((item) =>
          item.id === itemId ? { ...item, status: newStatus } : item
        );

      switch (selectedCategory) {
        case "Services":
          setServices(updateItem(services));
          break;
        case "Jobs":
          setJobs(updateItem(jobs));
          break;
        case "Events":
          setEvents(updateItem(events));
          break;
        case "Offers":
          setOffers(updateItem(offers));
          break;
        case "Classifieds":
          setClassifieds(updateItem(classifieds));
          break;
        case "sponsored-ads":
          setAds(updateItem(ads));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const renderCategoryData = (items) => {
    if (!items || items.length === 0) {
      return (
        <Typography variant="h6" align="center" mt={4}>
          No {selectedCategory} available.
        </Typography>
      );
    }

    return (
      <Grid container spacing={3}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card sx={{ boxShadow: 3, p: 2, mt: 5 }}>
              <CardContent>
                {selectedCategory === "Services" && (
                  <>
                    <Typography variant="h6" fontWeight="bold">
                      Service Name: {item.service_name}
                    </Typography>
                    <Typography>Price: {item.price}</Typography>
                    <Typography>Location: {item.location}</Typography>
                    <Typography>Description: {item.description}</Typography>
                    <Typography>
                      Applied on: {new Date(item.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography>Status: {item.status}</Typography>
                  </>
                )}
                {selectedCategory === "Jobs" && (
                  <>
                    <Typography variant="h6" fontWeight="bold">
                      Job Title: {item.job_title}
                    </Typography>
                    <Typography>Company Name: {item.company}</Typography>
                    <Typography>Location: {item.location}</Typography>
                    <Typography>Company Website: {item.company_website}</Typography>
                    <Typography>Description: {item.description}</Typography>
                    <Typography>Job Type: {item.job_type}</Typography>
                    <Typography>
                      Applied on: {new Date(item.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography>Status: {item.status}</Typography>
                  </>
                )}
                {selectedCategory === "Events" && (
                  <>
                    <Typography variant="h6" fontWeight="bold">
                      Event Title: {item.name}
                    </Typography>
                    <Typography>Location: {item.location}</Typography>
                    <Typography>Description: {item.description}</Typography>
                    <Typography>Event Fees: {item.event_fees}</Typography>
                    <Typography>Event Type: {item.event_type}</Typography>
                    <Typography>
                      Date: {new Date(item.event_date).toLocaleDateString()}
                    </Typography>
                    <Typography>Status: {item.status}</Typography>
                  </>
                )}
                {selectedCategory === "Offers" && (
                  <>
                    <Typography variant="h6" fontWeight="bold">
                      Offer Name: {item.title}
                    </Typography>
                    <Typography>Business: {item.business}</Typography>
                    <Typography>Category: {item.category}</Typography>
                    <Typography>Original Price: {item.original_price}</Typography>
                    <Typography>Discount Price: {item.discount_price}</Typography>
                    <Typography>Location: {item.location}</Typography>
                    <Typography>Description: {item.description}</Typography>
                    <Typography>Valid Upto: {item.expiry_date}</Typography>
                  </>
                )}
                {selectedCategory === "Classifieds" && (
                  <>
                    <Typography variant="h6" fontWeight="bold">
                      Title: {item.title}
                    </Typography>
                    <Typography>Category: {item.category}</Typography>
                    <Typography>Price: {item.price}</Typography>
                    <Typography>Location: {item.city}</Typography>
                    <Typography>Description: {item.description}</Typography>
                  </>
                )}
                {selectedCategory === "sponsored-ads" && (
                  <>
                    <Typography variant="h6" fontWeight="bold">
                      Ad Title: {item.title}
                    </Typography>
                    <Typography>Business Name: {item.business}</Typography>
                    <Typography>Price : {item.price}</Typography>
                    <Typography>Category: {item.category}</Typography>
                    <Typography>Description: {item.description}</Typography>
                    <Typography>Location: {item.location}</Typography>
                    <Typography>
                      Date: {new Date(item.created_at).toLocaleDateString()}
                    </Typography>
                  </>
                )}
              </CardContent>
              <Stack direction="row" spacing={1} mt={2} mb={2} ml={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleStatusChange(item.id, "active")}
                  disabled={item.status === "active"}
                >
                  Active
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleStatusChange(item.id, "inactive")}
                  disabled={item.status === "inactive"}
                >
                  Inactive
                </Button>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const getItemsByCategory = () => {
    switch (selectedCategory) {
      case "Services":
        return services;
      case "Jobs":
        return jobs;
      case "Events":
        return events;
      case "Offers":
        return offers;
      case "Classifieds":
        return classifieds;
      case "sponsored-ads":
        return ads;
      default:
        return [];
    }
  };

  return (
    <Container sx={{ mt: 4, mb:4 }}>
      <Typography variant="h4" gutterBottom sx={{textAlign:"center",color:"#42326e",fontWeight:"bold"}}>
        My Opportunities
      </Typography>

      <Tabs
        value={selectedCategory}
        onChange={handleCategoryChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Services" value="Services" />
        <Tab label="Jobs" value="Jobs" />
        <Tab label="Events" value="Events" />
        <Tab label="Offers" value="Offers" />
        <Tab label="Classifieds" value="Classifieds" />
        <Tab label="Sponsored Ads" value="sponsored-ads" />
      </Tabs>

      {loading ? (
        <Stack alignItems="center" mt={5}>
          <CircularProgress />
        </Stack>
      ) : error ? (
        <Typography color="error" mt={3}>
          {error}
        </Typography>
      ) : (
        renderCategoryData(getItemsByCategory())
      )}
    </Container>
  );
};

export default MyOpportunities;
